import { Checkbox, FormControlLabel, Stack } from '@mui/material'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import IntakeCard from 'candidate/components/IntakeCard'
import React, { useEffect, useState } from 'react'
import NextButton from 'shared/components/NextButton'
import { getPxAsRem, isValidGreScore, isValidSatScore } from 'shared/utils'

export interface StandardizedTestScoreAnswer {
    satMathScore: number | undefined
    satVerbalScore: number | undefined
    satWritingScore: number | undefined
    satTotal: number | undefined
    greQuantitativeScore: number | undefined
    greVerbalScore: number | undefined
    greWritingScore: number | undefined
    greTotalScore: number | undefined
    actScore: number | undefined
    gmatScore: number | undefined
    tookActSatTests: boolean | undefined
    remembersTakingActSatTests: boolean | undefined
}
interface Props {
    answer?: StandardizedTestScoreAnswer
    callback: React.Dispatch<any>
    prompt: string
    skipCallback?: React.Dispatch<React.SetStateAction<string>>
    skipText?: string
}

const StandardizedTestScoreQuestion: React.FC<Props> = ({
    answer,
    callback,
    prompt,
    skipCallback,
    skipText,
}: Props) => {
    const [satMathScore, setSatMathScore] = useState<number | undefined>(
        answer?.satMathScore
    )
    const [satVerbalScore, setSatVerbalScore] = useState<number | undefined>(
        answer?.satVerbalScore
    )
    const [satWritingScore, setSatWritingScore] = useState<number | undefined>(
        answer?.satWritingScore
    )
    const [satTotal, setSatTotal] = useState<number | undefined>(
        answer?.satWritingScore
    )
    const [greQuantitativeScore, setGreQuantitativeScore] = useState<
        number | undefined
    >(answer?.greQuantitativeScore)
    const [greVerbalScore, setGreVerbalScore] = useState<number | undefined>(
        answer?.greVerbalScore
    )
    const [greWritingScore, setGreWritingScore] = useState<number | undefined>(
        answer?.greWritingScore
    )
    const [greTotalScore, setGreTotalScore] = useState<number | undefined>(
        answer?.greWritingScore
    )
    const [actScore, setACTScore] = useState<number | undefined>(
        answer?.actScore
    )
    const [gmatScore, setGMATScore] = useState<number | undefined>(
        answer?.gmatScore
    )
    const [tookActSatTests, setTookActSatTests] = useState<boolean | undefined>(
        answer?.tookActSatTests ?? true
    )
    const [remembersTakingActSatTests, setRemembersTakingActSatTests] =
        useState<boolean | undefined>(
            answer?.remembersTakingActSatTests ?? true
        )
    const [satMathError, setSatMathError] = useState<boolean>(false)
    const [satVerbalError, setSatVerbalError] = useState<boolean>(false)
    const [satWritingError, setSatWritingError] = useState<boolean>(false)
    const [satTotalError, setSatTotalError] = useState<boolean>(false)
    const [greQuantitativeError, setGreQuantitativeError] =
        useState<boolean>(false)
    const [greVerbalError, setGreVerbalError] = useState<boolean>(false)
    const [greWritingError, setGreWritingError] = useState<boolean>(false)
    const [greTotalError, setGreTotalError] = useState<boolean>(false)
    const [actError, setACTError] = useState<boolean>(false)
    const [gmatError, setGMATError] = useState<boolean>(false)

    useEffect(() => {
        if (answer) {
            setSatMathScore(answer.satMathScore)
            setSatVerbalScore(answer.satVerbalScore)
            setSatWritingScore(answer.satWritingScore)
            setSatTotal(answer.satTotal)
            setGreQuantitativeScore(answer.greQuantitativeScore)
            setGreVerbalScore(answer.greVerbalScore)
            setGreWritingScore(answer.greWritingScore)
            setGreTotalScore(answer.greTotalScore)
            setACTScore(answer.actScore)
            setGMATScore(answer.gmatScore)
            setRemembersTakingActSatTests(answer.remembersTakingActSatTests)
            setTookActSatTests(answer.tookActSatTests)
        }
    }, [answer, prompt])

    const submitData = () => {
        const response: StandardizedTestScoreAnswer = {
            satMathScore,
            satVerbalScore,
            satWritingScore,
            satTotal,
            greQuantitativeScore,
            greVerbalScore,
            greWritingScore,
            greTotalScore,
            actScore,
            gmatScore,
            tookActSatTests,
            remembersTakingActSatTests,
        }
        if (tookActSatTests === false || remembersTakingActSatTests === false) {
            response.satMathScore = undefined
            response.satVerbalScore = undefined
            response.satWritingScore = undefined
            response.satTotal = undefined
            response.actScore = undefined
        }
        if (tookActSatTests === false) {
            response.remembersTakingActSatTests = true
        }
        if (remembersTakingActSatTests === false) {
            response.tookActSatTests = true
        }
        callback({ response })
    }

    const nextButtonIsDisabled = React.useMemo(() => {
        // If any errors are present, disable the next button
        if (
            satMathError ||
            satVerbalError ||
            satWritingError ||
            satTotalError ||
            greQuantitativeError ||
            greVerbalError ||
            greWritingError ||
            greTotalError ||
            actError ||
            gmatError
        ) {
            return true
        }

        // must have a checkbox or either sat or act scores
        if (
            remembersTakingActSatTests &&
            tookActSatTests &&
            !satMathScore &&
            !satVerbalScore &&
            !satWritingScore &&
            !satTotal &&
            !actScore
        ) {
            return true
        }

        return false
    }, [
        satMathError,
        satVerbalError,
        satWritingError,
        satTotalError,
        greQuantitativeError,
        greVerbalError,
        greWritingError,
        actError,
        gmatError,
        satMathScore,
        satVerbalScore,
        satWritingScore,
        satTotal,
        greTotalError,
        actScore,
        tookActSatTests,
        remembersTakingActSatTests,
    ])

    return (
        <>
            <IntakeCard
                question={prompt}
                skipCallback={skipCallback}
                skipText={skipText}
            >
                <Container
                    style={{
                        display: 'inline-block',
                        alignContent: 'left',
                        marginBottom: getPxAsRem(36),
                        marginTop: '1rem',
                        padding: '0',
                        width: '100%',
                        maxWidth: '100%',
                    }}
                >
                    {/* SAT */}
                    <div
                        style={{
                            display: 'flex',
                            marginBottom: '2rem',
                            justifyContent: 'space-between',
                        }}
                    >
                        <TextField
                            id="sat-math"
                            label="SAT Math"
                            type="number"
                            inputProps={{
                                type: 'number',
                                min: 200,
                                max: 800,
                                step: 10,
                            }}
                            disabled={
                                remembersTakingActSatTests === false ||
                                tookActSatTests === false
                            }
                            value={satMathScore ?? ''}
                            helperText="Please enter a valid SAT Math score between 200 and 800"
                            error={satMathError}
                            onChange={(_e) => {
                                if (_e.target.value) {
                                    const mathScore = parseInt(
                                        _e.target.value.toString()
                                    )
                                    setSatMathScore(mathScore)
                                    if (isValidSatScore(mathScore)) {
                                        setSatMathError(false)
                                    } else {
                                        setSatMathError(true)
                                    }
                                } else {
                                    setSatMathScore(undefined)
                                    setSatMathError(false)
                                }
                            }}
                        />

                        <div style={{ width: '1.5rem' }}></div>

                        <TextField
                            id="sat-verbal"
                            label="SAT Verbal"
                            inputProps={{
                                type: 'number',
                                min: 200,
                                max: 800,
                                step: 10,
                            }}
                            disabled={
                                remembersTakingActSatTests === false ||
                                tookActSatTests === false
                            }
                            error={satVerbalError}
                            value={satVerbalScore ?? ''}
                            helperText="Please enter a valid SAT Verbal score between 200 and 800"
                            onChange={(_e) => {
                                if (_e.target.value) {
                                    const verbalScore = parseInt(
                                        _e.target.value.toString()
                                    )
                                    setSatVerbalScore(verbalScore)
                                    if (isValidSatScore(verbalScore)) {
                                        setSatVerbalError(false)
                                    } else {
                                        setSatVerbalError(true)
                                    }
                                } else {
                                    setSatVerbalScore(undefined)
                                    setSatVerbalError(false)
                                }
                            }}
                        />

                        <div style={{ width: '1.5rem' }}></div>

                        <TextField
                            id="sat-writing"
                            label="SAT Writing"
                            inputProps={{
                                type: 'number',
                                min: 200,
                                max: 800,
                                step: 10,
                            }}
                            disabled={
                                remembersTakingActSatTests === false ||
                                tookActSatTests === false
                            }
                            error={satWritingError}
                            value={satWritingScore ?? ''}
                            helperText="Please enter a valid SAT Writing score between 200 and 800"
                            onChange={(_e) => {
                                if (_e.target.value) {
                                    const writingScore = parseInt(
                                        _e.target.value.toString()
                                    )
                                    setSatWritingScore(writingScore)
                                    if (isValidSatScore(writingScore)) {
                                        setSatWritingError(false)
                                    } else {
                                        setSatWritingError(true)
                                    }
                                } else {
                                    setSatWritingScore(undefined)
                                    setSatWritingError(false)
                                }
                            }}
                        />
                    </div>
                    <div style={{ marginBottom: '2rem' }}>
                        <TextField
                            fullWidth
                            id="satTotal"
                            label="SAT Total"
                            inputProps={{
                                type: 'number',
                                min: 600,
                                max: 2400,
                                step: 10,
                            }}
                            disabled={
                                remembersTakingActSatTests === false ||
                                tookActSatTests === false
                            }
                            value={satTotal ?? ''}
                            error={satTotalError}
                            helperText="Please enter a valid SAT score between 600 and 2400"
                            onChange={(_e) => {
                                if (_e.target.value) {
                                    const satTotal = parseInt(
                                        _e.target.value.toString()
                                    )
                                    setSatTotal(satTotal)
                                    if (satTotal >= 600 && satTotal <= 2400) {
                                        setSatTotalError(false)
                                    } else {
                                        setSatTotalError(true)
                                    }
                                } else {
                                    setSatTotal(undefined)
                                    setSatTotalError(false)
                                }
                            }}
                        />
                    </div>
                    {/* ACT */}
                    <div style={{ marginBottom: '2rem' }}>
                        <TextField
                            fullWidth
                            id="act"
                            label="ACT"
                            inputProps={{
                                type: 'number',
                                min: 1,
                                max: 36,
                                step: 1,
                            }}
                            disabled={
                                remembersTakingActSatTests === false ||
                                tookActSatTests === false
                            }
                            value={actScore ?? ''}
                            error={actError}
                            helperText="Please enter a valid ACT score between 1 and 36"
                            onChange={(_e) => {
                                if (_e.target.value) {
                                    const actScore = parseInt(
                                        _e.target.value.toString()
                                    )
                                    setACTScore(actScore)
                                    if (actScore >= 1 && actScore <= 36) {
                                        setACTError(false)
                                    } else {
                                        setACTError(true)
                                    }
                                } else {
                                    setACTScore(undefined)
                                    setACTError(false)
                                }
                            }}
                        />
                    </div>
                    {/* GRE */}
                    <div
                        style={{
                            display: 'flex',
                            marginBottom: '2rem',
                            justifyContent: 'space-between',
                        }}
                    >
                        <TextField
                            id="gre-math"
                            label="GRE Quantitative"
                            type="number"
                            inputProps={{
                                type: 'number',
                                min: 130,
                                max: 170,
                                step: 1,
                            }}
                            value={greQuantitativeScore ?? ''}
                            helperText="Please enter a valid GRE Quantitative score between 130 and 170"
                            error={greQuantitativeError}
                            onChange={(_e) => {
                                if (_e.target.value) {
                                    const score = parseInt(
                                        _e.target.value.toString()
                                    )
                                    setGreQuantitativeScore(score)
                                    if (isValidGreScore(score)) {
                                        setGreQuantitativeError(false)
                                    } else {
                                        setGreQuantitativeError(true)
                                    }
                                } else {
                                    setGreQuantitativeScore(undefined)
                                    setGreQuantitativeError(false)
                                }
                            }}
                        />

                        <div style={{ width: '1.5rem' }}></div>

                        <TextField
                            id="gre-verbal"
                            label="GRE Verbal"
                            inputProps={{
                                type: 'number',
                                min: 130,
                                max: 170,
                                step: 1,
                            }}
                            error={greVerbalError}
                            value={greVerbalScore ?? ''}
                            helperText="Please enter a valid GRE Verbal score between 130 and 170"
                            onChange={(_e) => {
                                if (_e.target.value) {
                                    const verbalScore = parseInt(
                                        _e.target.value.toString()
                                    )
                                    setGreVerbalScore(verbalScore)
                                    if (isValidGreScore(verbalScore)) {
                                        setGreVerbalError(false)
                                    } else {
                                        setGreVerbalError(true)
                                    }
                                } else {
                                    setGreVerbalScore(undefined)
                                    setGreVerbalError(false)
                                }
                            }}
                        />

                        <div style={{ width: '1.5rem' }}></div>

                        <TextField
                            id="gre-writing"
                            label="GRE Writing"
                            inputProps={{
                                type: 'number',
                                min: 0,
                                max: 6,
                                step: 0.5,
                            }}
                            error={greWritingError}
                            value={greWritingScore ?? ''}
                            helperText="Please enter a valid GRE Writing score between 0 and 6"
                            onChange={(_e) => {
                                if (_e.target.value) {
                                    const writingScore = parseInt(
                                        _e.target.value.toString()
                                    )
                                    setGreWritingScore(writingScore)
                                    if (isValidGreScore(writingScore, true)) {
                                        setGreWritingError(false)
                                    } else {
                                        setGreWritingError(true)
                                    }
                                } else {
                                    setGreWritingScore(undefined)
                                    setGreWritingError(false)
                                }
                            }}
                        />
                    </div>
                    <div style={{ marginBottom: '2rem' }}>
                        <TextField
                            fullWidth
                            id="greTotal"
                            label="GRE Total"
                            inputProps={{
                                type: 'number',
                                min: 260,
                                max: 340,
                                step: 1,
                            }}
                            value={greTotalScore ?? ''}
                            error={greTotalError}
                            helperText="Please enter a valid GRE score between 260 and 340"
                            onChange={(_e) => {
                                if (_e.target.value) {
                                    const score = parseInt(
                                        _e.target.value.toString()
                                    )
                                    setGreTotalScore(score)
                                    if (score >= 260 && score <= 340) {
                                        setGreTotalError(false)
                                    } else {
                                        setGreTotalError(true)
                                    }
                                } else {
                                    setGreTotalScore(undefined)
                                    setGreTotalError(false)
                                }
                            }}
                        />
                    </div>
                    {/* GMAT */}
                    <div style={{ marginBottom: '2rem' }}>
                        <TextField
                            fullWidth
                            id="gmat"
                            label="GMAT"
                            inputProps={{
                                type: 'number',
                                min: 205,
                                max: 805,
                                step: 5,
                            }}
                            value={gmatScore ?? ''}
                            error={gmatError}
                            helperText="Please enter a valid GMAT score between 205 and 805"
                            onChange={(_e) => {
                                if (_e.target.value) {
                                    const gmatScore = parseInt(
                                        _e.target.value.toString()
                                    )
                                    setGMATScore(gmatScore)
                                    if (gmatScore >= 205 && gmatScore <= 805) {
                                        setGMATError(false)
                                    } else {
                                        setGMATError(true)
                                    }
                                } else {
                                    setGMATScore(undefined)
                                    setGMATError(false)
                                }
                            }}
                        />
                    </div>
                    <Stack>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={(e) => {
                                        setRemembersTakingActSatTests(
                                            !e.target.checked
                                        )
                                    }}
                                    checked={
                                        remembersTakingActSatTests === false
                                    }
                                />
                            }
                            disabled={tookActSatTests === false}
                            label="I do not remember my ACT/SAT score"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={(e) => {
                                        setTookActSatTests(!e.target.checked)
                                    }}
                                    checked={tookActSatTests === false}
                                />
                            }
                            disabled={remembersTakingActSatTests === false}
                            label="I did not take the SAT/ACT"
                        />
                    </Stack>
                </Container>
                <NextButton
                    disabled={nextButtonIsDisabled}
                    onClickHandler={submitData}
                />
            </IntakeCard>
        </>
    )
}

export default StandardizedTestScoreQuestion
